import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import BlankImage from '../../../../assets/images/blank-image.png';
import { RiveAnimation } from '../../../../components/common/animations/RiveAnimation';
import { Button } from '../../../../components/common/buttons/Button';
import { Icon } from '../../../../components/common/icon/Icon';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import {
  IProductForInquiryModal,
  ProductInquiryModal,
} from '../../../../components/custom/modal/product/ProductInquiryModal';
import { pageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ProductStatusConstant } from '../../../../constants/product/product.constant';
import { useHandleProductInquiry } from '../../../../hooks/product/useHandleProductInquiry';
import { useAdvertisementConsent } from '../../../../hooks/user/useAdvertisementConsent';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { IProduct } from '../../../../interfaces/Product.interfaces';
import { browserStorage } from '../../../../utils/browserStorage.util';
import { isNewProduct } from '../../../../utils/calculator/calculateDate.util';
import {
  getGeneralMarketDetailPageURL,
  IProductForGetDetailPageURL,
} from '../../../../utils/getter/getProductDetailPageURL.util';
import { getGeneralProductPrice, IProductForGetProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { LoginToCheckPrice } from '../components/LoginToCheckPrice';
import { ProductChip } from '../components/ProductChip';
import { ProductStatusText } from '../components/ProductStatusText';
import { ProductSeparatedThumbnailImage } from './components/ProductSeparatedThumbnailImage';

export type IProductForDetailCard = Pick<
  IProduct,
  | 'id'
  | 'machine'
  | 'code'
  | 'status'
  | 'images'
  | 'category'
  | 'title'
  | 'options'
  | 'location'
  | 'manufacturedYear'
  | 'operationTime'
  | 'price'
  | 'priceVisible'
  | 'startOnSaleDate'
  | 'metadata'
  | 'soldOutDate'
>;

interface ProductDetailCardProps {
  product: IProductForDetailCard;
}
const { PENDING, SOLDOUT, IN_CONTRACT, ONSALE } = ProductStatusConstant;

export const ProductDetailCard = ({ product }: ProductDetailCardProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();

  const updateAdConsent = useAdvertisementConsent('PLP');

  const [showProductPendingAlarmSettingModal, setShowProductPendingAlarmSettingModal] = useState<boolean>(false);
  const [showProductPendingAlarmSoonModal, setShowProductPendingAlarmSoonModal] = useState<boolean>(false);

  const [isProductInquiryModalOpen, setIsProductInquiryModalOpen] = useState<boolean>(false);

  const clickedPendingProductId = browserStorage.getItem({
    storageType: 'sessionStorage',
    itemKey: 'clickedPendingProductId',
  });

  const { handleProductInquiryButtonClick } = useHandleProductInquiry({
    product,
    pageType: 'plp',
  });

  const price = useMemo<string>(() => {
    return getGeneralProductPrice({ product: product as IProductForGetProductPrice, isUserLoggedIn });
  }, [product]);

  const productDetailPageURL = useMemo<string>(() => {
    return getGeneralMarketDetailPageURL(product as IProductForGetDetailPageURL);
  }, [product.category]);

  const isSoldOutProduct = product.status === SOLDOUT;

  useEffect(() => {
    if (clickedPendingProductId && product.id && clickedPendingProductId === product.id) {
      browserStorage.setItem({
        storageType: 'sessionStorage',
        itemKey: 'clickedPendingProductId',
        value: '',
      });

      if (!isUserLoggedIn || !userProfile?.advertisementTermsAgreement) {
        setShowProductPendingAlarmSettingModal(true);
        return;
      }
      setShowProductPendingAlarmSoonModal(true);
    }
  }, [product.id, isUserLoggedIn, userProfile?.advertisementTermsAgreement, clickedPendingProductId]);

  return (
    <>
      <Container
        onClick={(e) => {
          e.stopPropagation();

          if (product.status === PENDING) {
            if (!isUserLoggedIn || !userProfile?.advertisementTermsAgreement) {
              setShowProductPendingAlarmSettingModal(true);
              return;
            }
            setShowProductPendingAlarmSoonModal(true);
          } else if (product.status !== SOLDOUT) {
            navigate(productDetailPageURL);
          }
        }}
      >
        <ImageWrap id={'product-list-swiper-container'}>
          {product.status === SOLDOUT && (
            <ProductStatusText
              text={'판매 완료'}
              {...(product.soldOutDate && {
                subText: format(new Date(product.soldOutDate), 'yy년 MM월'),
              })}
            />
          )}
          {product.status === IN_CONTRACT && <ProductStatusText text={'계약 진행 중'} />}

          {product.status === ONSALE ? (
            <CustomSwiper
              slidesPerView={1}
              edgeSwipeDetection
              loop
              autoplay={{ delay: 3000 }}
              pagination={product.images.filter((image) => image.type === 'LIST').length > 1}
            >
              {product.images
                .filter((image) => image.type === 'LIST')
                .map((image) => (
                  <CustomSwiperSlide>
                    <ProductImage src={image.imageUrl} alt={image.id} onClick={() => {}} loading={'lazy'} />
                    <div className={'swiper-lazy-preloader'} />
                  </CustomSwiperSlide>
                ))}
            </CustomSwiper>
          ) : (
            <Image
              src={
                product.images.filter((image) => image.type === 'LIST').length > 0
                  ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
                  : BlankImage
              }
              alt={
                product.images.filter((image) => image.type === 'LIST').length > 0
                  ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
                  : BlankImage
              }
              isBlurred={product.status === SOLDOUT || product.status === IN_CONTRACT}
              loading={'lazy'}
            />
          )}

          <ProductSeparatedThumbnailImage productImages={product.images} />

          <BadgeContainer>
            <ProductChip type={product.category as 'PLUVA' | 'PARTNER'} />
            {isNewProduct(product.startOnSaleDate!, product.status === ONSALE) && <ProductChip type={'NEW'} />}
          </BadgeContainer>
        </ImageWrap>

        <InstallmentSection isBlurred={isSoldOutProduct}>
          <InstallmentLeftSection>
            {!isSoldOutProduct ? (
              <RiveAnimation name={'CircleWonAnimate'} size={'Large'} />
            ) : (
              <Icon name={'CircleWon'} size={'Large'} fill={'#445EFF'} />
            )}

            <InstallmentTitleWrapper>
              <InstallmentTitle fontWeight={700}>{'할부가 가능한 매물'}</InstallmentTitle>
              <InstallmentTitle fontWeight={500}>{'이예요!'}</InstallmentTitle>
            </InstallmentTitleWrapper>
          </InstallmentLeftSection>

          <Button
            icon={{ fileName: 'AngleRight', position: 'right', fill: '#505363', size: 'Small' }}
            borderType={'round'}
            color={'filledGreyLight'}
            size={'xs'}
            text={{ size: 'sm', content: '할부 문의 하기' }}
            isDisabled={isSoldOutProduct}
            onClick={() => {
              handleProductInquiryButtonClick();
              if (!isUserLoggedIn) {
                setIsProductInquiryModalOpen(true);
              }
            }}
          />
        </InstallmentSection>

        <ProductInfo isBlurred={isSoldOutProduct}>
          <Title>{product.title}</Title>

          {!isSoldOutProduct && (
            <>
              {product.metadata && (
                <MetadataContainer>
                  {product.metadata
                    .split(',')
                    .map((metadata) => metadata.trim())
                    .map((metadata, index, array) => {
                      return (
                        <>
                          <Metadata key={metadata}>{metadata}</Metadata>
                          {index !== array.length - 1 && <Metadata>{'·'}</Metadata>}
                        </>
                      );
                    })}
                </MetadataContainer>
              )}

              {(product.location || product.manufacturedYear || product.operationTime) && (
                <TagsContainer>
                  {product.location && (
                    <Tag>
                      <Icon name={'Marker'} width={14} height={14} />
                      <TagText>{product.location}</TagText>
                    </Tag>
                  )}

                  {product.manufacturedYear && (
                    <Tag>
                      <Icon name={'CalendarDaysColorRed'} size={'Medium'} />
                      <TagText>{`${product.manufacturedYear}년식`}</TagText>
                    </Tag>
                  )}

                  {product.operationTime && (
                    <Tag>
                      <Icon name={'ClockFiveDuotone'} size={'Medium'} />
                      <TagText>{`${product.operationTime.toLocaleString()}시간`}</TagText>
                    </Tag>
                  )}
                </TagsContainer>
              )}
            </>
          )}

          <PriceSection>
            <PriceTitleContainer>
              <PriceWrap>
                {isUserLoggedIn && <Price>{price}</Price>}
                {!isUserLoggedIn && <LoginToCheckPrice />}
              </PriceWrap>
            </PriceTitleContainer>
          </PriceSection>
        </ProductInfo>
      </Container>

      {isProductInquiryModalOpen && (
        <ProductInquiryModal
          product={product as IProductForInquiryModal}
          pageType={'plp'}
          onModalClose={() => setIsProductInquiryModalOpen(false)}
        />
      )}

      <PopupConfirmModal
        isShow={showProductPendingAlarmSettingModal}
        hasCloseButton={false}
        closeModal={() => setShowProductPendingAlarmSettingModal(false)}
        title={'🔔 판매 예정 매물 알림 받기'}
        description={'지금 입고 알림을 신청하시면\n매물이 입고될 때 알림을 보내드려요!'}
        buttonArray={[
          {
            text: !isUserLoggedIn ? '로그인 하고 알림 받기' : '마케팅 수신 동의하고 알림받기',
            onClick: () => {
              if (!isUserLoggedIn) {
                browserStorage.setItem({
                  storageType: 'sessionStorage',
                  itemKey: 'clickedPendingProductId',
                  value: product.id,
                });
                navigate(pageInfo.loginPage.path);
                return;
              }

              updateAdConsent({
                isAgreeing: true,
                onSuccess: () => {
                  setShowProductPendingAlarmSettingModal(false);
                  setShowProductPendingAlarmSoonModal(true);
                },
              });
            },
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => setShowProductPendingAlarmSettingModal(false),
            color: 'gray',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={showProductPendingAlarmSoonModal}
        hasCloseButton={false}
        closeModal={() => setShowProductPendingAlarmSoonModal(false)}
        title={'🔔 입고시 알림을 드릴게요!'}
        description={'매물이 업로드 되면\n가장 먼저 알림을 보내드릴게요!'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => setShowProductPendingAlarmSoonModal(false),
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  z-index: 1;

  gap: 16px;

  &:hover {
    cursor: pointer;
  }
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 10px;
  left: 10px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const ImageWrap = styled.div`
  width: 100%;

  padding-top: 75%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductInfo = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 6px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const Title = styled.h1`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */

  overflow: hidden;
  text-overflow: ellipsis;

  display: block;
  white-space: nowrap;
  width: 100%;
`;

const PriceSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  gap: 16px;
`;

const PriceTitleContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const Price = styled.span`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const DiscountWrap = styled.div`
  display: flex;
  align-items: center;
`;

const Discount = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 15.6px */
`;

const SrollSection = styled.div``;

const CustomSwiper = styled(Swiper)`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  height: 100%;

  border-radius: 8px;
  cursor: pointer;

  margin-bottom: 22px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;

  border-radius: 8px;
  cursor: pointer;
`;

const TagsContainer = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  align-items: center;
  gap: var(--radius-medium, 8px);
`;

const Tag = styled.div`
  display: flex;
  padding: var(--radius-small, 4px) 6px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const TagText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const MetadataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const Metadata = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const InstallmentSection = styled.div<{ isBlurred: boolean }>`
  display: flex;
  padding: var(--radius-medium, 8px);
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  border-radius: var(--radius-medium, 8px);

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const InstallmentLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const InstallmentTitleWrapper = styled.div`
  display: flex;
`;

const InstallmentTitle = styled.span<{ fontWeight: number }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;

  overflow: hidden;
  color: var(--Light-Scheme-Color-primary-blue, #232429);
  text-overflow: ellipsis;

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: ${(props) => props.fontWeight};
  line-height: 130%; /* 18.2px */
`;
